import { useAuth } from "react-oidc-context";
import { useActiveOrg, NoAccessScreen, canUseApp } from "common-fe";
import { Box, CircularProgress } from "@mui/material";
import { handleLogout } from "../util/logout";

// AC#4 (#397) + #531: only a Fox Village super admin OR a member of a
// score-entitled org who ALSO holds the score-admin role may use scoreadmin.
// The rule (the access token's `entitlements` claim holds "score" for the
// active org AND its `roles` claim holds score_admin) lives in common-fe's
// canUseApp (#3656), the ONE copy the avatar-menu app switcher shares so it
// never offers an app this gate then refuses. The API independently enforces
// the same gate (#400, #401); this is the matching FE affordance so a
// non-entitled user gets a clear message, not empty screens.

export default function ScoreEntitlementGate({ children }) {
  const auth = useAuth();
  const { activeOrgId, isSuper, orgResolved } = useActiveOrg();
  const accessToken = auth.user?.access_token;

  // #2120 (approved by Jaybill 2026-08-30, card comment): decide off
  // `orgResolved`, NOT the org-list network fetch (`loading`). For a
  // returning user, activeOrgId is already known synchronously from
  // localStorage (common-fe ActiveOrgContext), so this gate can check the
  // token's OWN entitlements/roles claims for that org immediately — no
  // waiting on the GET /org round trip. Accepted trade: if the active org's
  // entitlement/role was revoked since the token was minted, this can let a
  // now-unentitled user in until they next get a fresh token (logout, or
  // token refresh) — deliberately not mitigated (see ActiveOrgContext.jsx).
  // Only a brand-new session with no stored org (activeOrgId still null)
  // waits here, because there is genuinely no org to check claims against
  // yet.
  if (!orgResolved) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Super bypasses (may act on any org). Everyone else needs BOTH the org's
  // score entitlement AND the score-admin role for the active org (#531).
  if (canUseApp("scoreadmin", { accessToken, orgId: activeOrgId, isSuper })) {
    return children;
  }

  // #881 / #3338 — a user whose ACTIVE org fails this check but IS entitled
  // on another org must still be able to log out and switch orgs from right
  // here. NoAccessScreen (common-fe, the one screen every gate shares) wraps
  // GateHeader for that, names the app, says the org admin grants access,
  // and links back to the Account Center apps page.
  return (
    <NoAccessScreen
      appName="Score Admin"
      onLogout={handleLogout}
      accountFeBaseUrl={import.meta.env.VITE_ACCOUNT_FE_BASE_URL}
      env={import.meta.env}
    />
  );
}
